import {
  Link as MuiLink,
  Typography,
  Autocomplete,
  TextField,
  IconButton,
  TablePagination,
  Chip,
  Divider,
  TableCell,
  TableHead,
  TableBody,
  Table,
  TableRow,
  MenuItem,
  Menu,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Snackbar,
  Alert,
  AlertTitle,
  Fab,
  TableContainer,
  Input,
  Skeleton,
} from '@mui/material'
import './LinesCustomView.css'
import ActionBar from '../Misc/ActionBar'
import { Lines, SnackbarState } from './Lines'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { formatCurrency } from '../helper'
import {
  AddBoxOutlined,
  ArrowBackOutlined,
  DifferenceOutlined,
  DeleteOutlined,
  MoreVert,
  ExpandMore,
  ExpandLess,
  ChevronRight,
  ChevronLeft,
  Add,
  DragIndicator,
} from '@mui/icons-material'
import { useContext, useEffect, useState } from 'react'
import { customViewTemplateType, quoteLineType } from '../types'
import { LineEdit } from './LineEdit'
import { LinesNewLine } from './LinesNewLine'
import axios from 'axios'
import { dataContext } from '..'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import dayjs from 'dayjs'

const COLUMN_MAP: Record<
  string,
  {
    property: keyof quoteLineType
    format?: 'currency' | 'percentage' | 'number' | 'date' | 'boolean' | 'string'
    size?: number
  }
> = {
  '#': { property: 'LineNumber', format: 'number' },
  Description: { property: 'Description' },
  Quantity: { property: 'Qty', format: 'number' },
  Qty: { property: 'Qty', format: 'number' },
  Provider: { property: 'Provider' },
  Manufacturer: { property: 'Manufacturer' },
  'Unit Cost': { property: 'UnitCost', format: 'currency' },
  'Ext Cost': { property: 'ExtCost', format: 'currency' },
  Margin: { property: 'Margin', format: 'percentage' },
  'Ext Price': { property: 'ExtPrice', format: 'currency' },
  'Contract Price': { property: 'ContractCeilingPrice', format: 'currency' },
  'Contract Catalog': { property: 'ContractCatalog' },
  Compliance: { property: 'Compliance' },
  'Mfr Part Number': { property: 'MfrPartNumber' },
  'Display Part Number': { property: 'DisplayPartNumber' },
  'Unit Price': { property: 'UnitPrice', format: 'currency' },
  Distributor: { property: 'Distributor' },
  MSRP: { property: 'MSRP', format: 'currency' },
  'OEM Code': { property: 'OEMCode' },
  CLIN: { property: 'CLIN' },
  'Product Type': { property: 'ProductType' },
  'Start Date': { property: 'StartDate', format: 'date' },
  'End Date': { property: 'EndDate', format: 'date' },
  'Service Tag': { property: 'ServiceTag' },
  'License SN': { property: 'LicenseSN' },
  'Unit Of Measure': { property: 'UnitOfMeasure' },
  Notes: { property: 'Notes' },
  TAA: { property: 'TAA' },
  'Country Of Origin': { property: 'CountryOfOrigin' },
  'Contract Number': { property: 'ContractNumber' },
  'Credit Card': { property: 'CreditCard', format: 'boolean' },
  'Total Fees': { property: 'TotalFees', format: 'currency' },
  Bundle: { property: 'Bundle' },
}

const getColumnWidth = (relativeWidth: number): string => {
  const baseWidth = 100
  return `${baseWidth * relativeWidth}px`
}

const getColumnValue = (item: quoteLineType, columnName: string): string => {
  const columnConfig = COLUMN_MAP[columnName]
  const value = item[columnConfig.property]

  if (!value) {
    return '-'
  }

  switch (columnConfig.format) {
    case 'currency':
      return typeof value === 'number'
        ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
        : String(value)
    case 'percentage':
      return typeof value === 'number' ? `${(value * 100).toFixed(2)}%` : String(value)
    case 'number':
      return typeof value === 'number' ? value.toLocaleString('en-US') : String(value)
    case 'date':
      return value instanceof Date ? value.toLocaleDateString() : String(value)
    case 'boolean':
      return String(value)
    default:
      return String(value)
  }
}

function LinesCustomView() {
  const {
    quote,
    lineItems,
    setLineItems,
    lineItemsLoading,
    setLineItemsLoading,
    sortedLineItems,
    selectedLineItems,
    setSelectedLineItems,
    setBulkEditSnackbar,
    setBundleSnackbar,
    lineCount,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    canDrag,
    setCanDrag,
    collapsedBundles,
    setCollapsedBundles,
    drawerState,
    setDrawerState,
    cloneSnackbar,
    setCloneSnackbar,
    deleteSnackbar,
    setDeleteSnackbar,
    bulkEditSnackbar,
    bulkDeleteSnackbar,
    setBulkDeleteSnackbar,
    addLineSnackbar,
    setAddLineSnackbar,
    bundleSnackbar,
    setUnbundleSnackbar,
    unbundleSnackbar,
    handleMenuAction,
    handleDrag,
    handleReloadLineItems,
    versions,
    differences,
  } = Lines()

  const navigate = useNavigate()
  const { user } = useContext(dataContext)
  const [templates, setTemplates] = useState<customViewTemplateType[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<customViewTemplateType | null>(null)
  const { quoteId } = useParams()
  const [loading, setLoading] = useState(false)
  const [templatesLoading, setTemplatesLoading] = useState(true)
  const [columns, setColumns] = useState<{ Name: string; Width: number; Order: number }[]>([])
  const [openDialog, setOpenDialog] = useState<string | null>(null)
  const [viewName, setViewName] = useState<string>('')
  const [openViewDrawer, setOpenViewDrawer] = useState(false)
  const [saveTemplateSnackbar, setSaveTemplateSnackbar] = useState<SnackbarState>(null)
  const [deleteTemplateSnackbar, setDeleteTemplateSnackbar] = useState<SnackbarState>(null)
  const [selectedAvailableFields, setSelectedAvailableFields] = useState<string[]>([])
  const [selectedFields, setSelectedFields] = useState<string[]>([])
  const [selectedSelectedFields, setSelectedSelectedFields] = useState<string[]>([])
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const [differencesView, setDifferencesView] = useState(false)
  const [additionsView, setAdditionsView] = useState(false)
  const [deletionsView, setDeletionsView] = useState(false)

  const defaultTemplate: customViewTemplateType = {
    TemplateName: 'Default',
    Columns: [
      { Name: '#', Width: 1, Order: 1 },
      { Name: 'Mfr Part Number', Width: 3, Order: 2 },
      { Name: 'Description', Width: 4, Order: 3 },
      { Name: 'Distributor', Width: 1, Order: 4 },
      { Name: 'Manufacturer', Width: 1, Order: 5 },
      { Name: 'Qty', Width: 1, Order: 6 },
      { Name: 'Unit Cost', Width: 1, Order: 7 },
      { Name: 'Ext Cost', Width: 1, Order: 8 },
      { Name: 'Margin', Width: 1, Order: 9 },
      { Name: 'Ext Price', Width: 1, Order: 10 },
      { Name: 'Contract Price', Width: 1, Order: 11 },
      { Name: 'Contract Catalog', Width: 1, Order: 12 },
      { Name: 'Compliance', Width: 1, Order: 13 },
    ],
  }

  const getTemplates = async () => {
    setTemplatesLoading(true)
    axios
      .get(`/getTemplates/${user.email}`)
      .then(response => {
        if (response.data.length > 0) {
          setTemplates([...response.data, defaultTemplate])
          setSelectedTemplate(defaultTemplate)
        } else {
          setTemplates([defaultTemplate])
          setSelectedTemplate(defaultTemplate)
        }
      })
      .catch(err => {
        console.error(err)
        setTemplates([])
        setColumns([])
        setSelectedTemplate(null)
      })
      .finally(() => {
        setTemplatesLoading(false)
      })
  }

  const applyView = () => {
    setLoading(true)
    setColumns(
      selectedFields.map(column => ({
        Name: column,
        Width: columnWidths[column] || 1,
        Order: selectedFields.indexOf(column) + 1,
      })),
    )
    setLoading(false)
  }

  const saveTemplate = async () => {
    setLoading(true)
    axios
      .post(`/saveTemplate`, {
        userEmail: user.email,
        templateName: viewName,
        columns: selectedFields.map(column => ({
          Name: column,
          Width: columnWidths[column] || 1,
          Order: selectedFields.indexOf(column) + 1,
        })),
      })
      .then(() => {
        setOpenDialog('')
        setSaveTemplateSnackbar('success')
        getTemplates()
      })
      .catch(err => {
        console.error(err)
        setSaveTemplateSnackbar('fail')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const deleteTemplate = async () => {
    setLoading(true)
    axios
      .delete(`/deleteTemplate`, {
        data: {
          userEmail: user.email,
          templateName: selectedTemplate?.TemplateName,
        },
      })
      .then(() => {
        setOpenDialog('')
        setDeleteTemplateSnackbar('success')
        getTemplates()
      })
      .catch(err => {
        console.error(err)
        setDeleteTemplateSnackbar('fail')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (!user.email) {
      return
    }
    getTemplates()
  }, [user.email])

  useEffect(() => {
    if (selectedTemplate) {
      setColumns(
        selectedTemplate.Columns.map(column => ({ Name: column.Name, Width: column.Width, Order: column.Order })),
      )
      setSelectedFields(selectedTemplate.Columns.map(column => column.Name))
      const widths: Record<string, number> = {}
      selectedTemplate.Columns.forEach(column => {
        widths[column.Name] = column.Width
      })
      setColumnWidths(widths)
    }
  }, [selectedTemplate])

  return (
    <div className="custom-view">
      <div className="custom-view-action-bar">
        <ActionBar
          lineItems={lineItems}
          setLineItems={setLineItems}
          selectedLineItems={selectedLineItems}
          setSelectedLineItems={setSelectedLineItems}
          setBulkEditSnackbar={setBulkEditSnackbar}
          setBundleSnackbar={setBundleSnackbar}
          lineItemCount={lineCount}
        />
      </div>
      <div className="custom-view-header-info">
        <Typography variant="body2">Quote Number:</Typography>
        {lineItemsLoading ? (
          <Skeleton width={120} height={25} />
        ) : (
          <MuiLink component={RouterLink} to={`/quote/${quote.QuoteId}`} variant="body1">
            {quote.QuoteNumber}
          </MuiLink>
        )}
        <Typography variant="body2">Contract Vehicle:</Typography>
        {lineItemsLoading ? (
          <Skeleton width={120} height={25} />
        ) : (
          <Typography variant="body1">{quote.ContractVehicle}</Typography>
        )}
        <Typography variant="body2">Total Price:</Typography>
        {lineItemsLoading ? (
          <Skeleton width={120} height={25} />
        ) : (
          <Typography variant="body1">{formatCurrency(quote.TotalCost)}</Typography>
        )}
        <Typography variant="body2">Total Cost:</Typography>
        {lineItemsLoading ? (
          <Skeleton width={120} height={25} />
        ) : (
          <Typography variant="body1">{formatCurrency(quote.TotalCost)}</Typography>
        )}
        <Typography variant="body2">Total Fees:</Typography>
        {lineItemsLoading ? (
          <Skeleton width={120} height={25} />
        ) : (
          <Typography variant="body1">{formatCurrency(quote.TotalFees)}</Typography>
        )}
        <Typography variant="body2">Gross Profit:</Typography>
        {lineItemsLoading ? (
          <Skeleton width={120} height={25} />
        ) : (
          <Typography variant="body1">{formatCurrency(quote.GrossProfit)}</Typography>
        )}
      </div>
      <div className="custom-view-header-buttons">
        <div className="custom-view-header-buttons-left">
          <IconButton onClick={() => navigate(`/quote/${quoteId}/lines`)}>
            <ArrowBackOutlined />
          </IconButton>
          {lineItemsLoading || versions.length === 0 ? (
            <Skeleton width={200} height={40} />
          ) : (
            <TextField
              label="Version"
              value={quoteId}
              onChange={e => {
                navigate(`/quote/${e.target.value}/lines/customView`)
                setSelectedLineItems([])
              }}
              size="small"
              select
            >
              {versions.map(
                (version: { Name: string | null; Version: number; CreatedDate: string; QuoteId: string }) => (
                  <MenuItem value={version.QuoteId} key={version.QuoteId}>
                    v{version.Version} {version.Name ? `- ${version.Name}` : ''} (
                    {dayjs(version.CreatedDate).format('MM/DD/YYYY hh:mm A')})
                  </MenuItem>
                ),
              )}
            </TextField>
          )}
          <Chip
            className="custom-view-header-buttons-differences-chip"
            label="Differences"
            icon={<DifferenceOutlined />}
            onClick={() => {
              setDifferencesView(!differencesView)
            }}
          />
          <Chip
            className="custom-view-header-buttons-additions-chip"
            label="Additions"
            icon={<AddBoxOutlined />}
            onClick={() => {
              setAdditionsView(!additionsView)
            }}
          />
          <Chip
            className="custom-view-header-buttons-deletions-chip"
            label="Deletions"
            icon={<DeleteOutlined />}
            onClick={() => {
              setDeletionsView(!deletionsView)
            }}
          />
          <Divider orientation="vertical" flexItem />
          {templatesLoading || templates.length === 0 ? (
            <Skeleton width={200} height={40} />
          ) : (
            <Autocomplete
              options={templates.map((template: { TemplateName: string }) => template.TemplateName)}
              value={selectedTemplate ? selectedTemplate.TemplateName : ''}
              onChange={(_, value) => {
                setSelectedTemplate(
                  templates.find((template: { TemplateName: string }) => template.TemplateName === value) ?? null,
                )
              }}
              renderInput={params => <TextField {...params} label="Display Template" />}
              size="small"
            />
          )}
          <IconButton
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              setDrawerState({ type: 'template-menu', anchorEl: e.currentTarget })
            }}
          >
            <MoreVert />
          </IconButton>
          <Menu
            open={drawerState.type === 'template-menu'}
            onClose={() => setDrawerState({ type: null })}
            anchorEl={drawerState.anchorEl}
          >
            <MenuItem
              onClick={() => {
                setOpenDialog('deleteView')
                setDrawerState({ type: null })
              }}
            >
              Delete Template
            </MenuItem>
            <MenuItem
              onClick={() => {
                setOpenViewDrawer(true)
                setDrawerState({ type: null })
              }}
            >
              Edit Display View
            </MenuItem>
            <MenuItem
              onClick={() => {
                setOpenDialog('saveView')
                setDrawerState({ type: null })
              }}
            >
              Save View
            </MenuItem>
          </Menu>
          <Divider orientation="vertical" flexItem />
          <Button
            variant="contained"
            onClick={async () => {
              if (lineItems.some(lineItem => !lineItem.LineItemId)) {
                await handleReloadLineItems()
                setCanDrag(!canDrag)
              } else {
                setCanDrag(!canDrag)
              }
            }}
          >
            {!canDrag ? 'Move Lines' : 'Lock Lines'}
          </Button>
        </div>
        <div className="custom-view-header-buttons-right">
          <TablePagination
            count={lineCount}
            onPageChange={(_, newPage) => {
              setPage(newPage)
            }}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onRowsPerPageChange={e => {
              setRowsPerPage(parseInt(e.target.value))
              setPage(0)
            }}
            component="div"
          />
        </div>
      </div>
      <DragDropContext
        onDragEnd={result => {
          handleDrag(result, lineItems, setLineItemsLoading)
        }}
      >
        <TableContainer>
          <Table stickyHeader>
            <TableHead className="custom-view-table-header">
              <TableRow>
                {columns.map(column => (
                  <TableCell
                    key={column.Name}
                    sx={{ width: getColumnWidth(column.Width), minWidth: getColumnWidth(column.Width) }}
                  >
                    {column.Name === '#' ? (
                      <Checkbox
                        checked={selectedLineItems.length === lineCount}
                        indeterminate={selectedLineItems.length > 0 && selectedLineItems.length < lineCount}
                        onChange={e => {
                          setSelectedLineItems(
                            e.target.checked
                              ? [
                                  ...lineItems.flatMap(item => item.children),
                                  ...lineItems.filter(item => item.LineNumber !== -1),
                                ]
                              : [],
                          )
                        }}
                      />
                    ) : null}
                    {column.Name}
                  </TableCell>
                ))}
                <TableCell key="Actions"></TableCell>
              </TableRow>
            </TableHead>
            {lineItemsLoading ? (
              <TableBody>
                {Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    {columns.map(column => {
                      const columnConfig = COLUMN_MAP[column.Name]
                      return (
                        <TableCell
                          key={column.Name}
                          align={
                            columnConfig.format === 'currency' ||
                            columnConfig.format === 'percentage' ||
                            (columnConfig.format === 'number' && column.Name !== '#')
                              ? 'right'
                              : 'left'
                          }
                          sx={{ width: getColumnWidth(column.Width), minWidth: getColumnWidth(column.Width) }}
                        >
                          <Skeleton variant="text" />
                        </TableCell>
                      )
                    })}
                    <TableCell key="Actions">
                      <Skeleton width={25} height={35} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <Droppable droppableId="all-items">
                {provided => {
                  const flatRenderItems: Array<{ item: quoteLineType; bundleId: string | null; isHeader: boolean }> = []
                  sortedLineItems.forEach(item => {
                    if (item.LineNumber === -1) {
                      flatRenderItems.push({
                        item: item,
                        bundleId: item.Bundle || null,
                        isHeader: true,
                      })
                      item.children.forEach(lineItem => {
                        flatRenderItems.push({ item: lineItem, bundleId: item.Bundle || null, isHeader: false })
                      })
                    } else {
                      flatRenderItems.push({ item: item, bundleId: null, isHeader: false })
                    }
                  })

                  return (
                    <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                      {flatRenderItems.map((renderItem, index) => {
                        let isLastChildInBundle = false
                        if (!renderItem.isHeader && renderItem.bundleId !== null) {
                          const fullBundle = lineItems.find(
                            item => item.Bundle === renderItem.bundleId && item.LineNumber === -1,
                          )
                          if (fullBundle) {
                            const lastChildInBundle = fullBundle.children[fullBundle.children.length - 1]
                            isLastChildInBundle = lastChildInBundle.LineItemId === renderItem.item.LineItemId
                          }
                        }

                        if (renderItem.isHeader) {
                          // Bundle header
                          const bundle = lineItems.find(item => item.LineItemId === renderItem.item.LineItemId)
                          return (
                            <Draggable
                              key={renderItem.item.LineItemId}
                              draggableId={renderItem.item.LineItemId}
                              index={index}
                              isDragDisabled={!canDrag}
                            >
                              {provided => (
                                <TableRow
                                  className="bundle-row"
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  {columns.map(column =>
                                    column.Name !== '#' ? (
                                      <TableCell
                                        key={column.Name}
                                        align={
                                          COLUMN_MAP[column.Name].format === 'currency' ||
                                          COLUMN_MAP[column.Name].format === 'percentage' ||
                                          (COLUMN_MAP[column.Name].format === 'number' && column.Name !== '#')
                                            ? 'right'
                                            : 'left'
                                        }
                                        sx={{
                                          width: getColumnWidth(column.Width),
                                          minWidth: getColumnWidth(column.Width),
                                        }}
                                      >
                                        {getColumnValue(renderItem.item, column.Name)}
                                      </TableCell>
                                    ) : (
                                      <TableCell
                                        key={column.Name}
                                        align="left"
                                        sx={{ width: getColumnWidth(column.Width), minWidth: '115px' }}
                                      >
                                        <Checkbox
                                          checked={
                                            selectedLineItems.filter(lineItem => lineItem.Bundle === bundle?.Bundle)
                                              .length === (bundle?.children.length ?? 0)
                                          }
                                          indeterminate={
                                            selectedLineItems.filter(lineItem => lineItem.Bundle === bundle?.Bundle)
                                              .length > 0 &&
                                            selectedLineItems.filter(lineItem => lineItem.Bundle === bundle?.Bundle)
                                              .length < (bundle?.children.length ?? 0)
                                          }
                                          onChange={() => {
                                            if (
                                              selectedLineItems.filter(
                                                lineItem =>
                                                  lineItem.Bundle === bundle?.Bundle || lineItem.LineNumber === -1,
                                              ).length > 0
                                            ) {
                                              setSelectedLineItems(prev =>
                                                prev.filter(
                                                  lineItem =>
                                                    lineItem.Bundle !== bundle?.Bundle && lineItem.LineNumber !== -1,
                                                ),
                                              )
                                            } else {
                                              setSelectedLineItems(prev => [...prev, ...(bundle?.children ?? [])])
                                            }
                                          }}
                                        />
                                        <IconButton
                                          onClick={() =>
                                            setCollapsedBundles(prev => {
                                              const newSet = new Set(prev)
                                              newSet.has(renderItem.item.Bundle ?? 'no-bundle')
                                                ? newSet.delete(renderItem.item.Bundle ?? 'no-bundle')
                                                : newSet.add(renderItem.item.Bundle ?? 'no-bundle')
                                              return newSet
                                            })
                                          }
                                        >
                                          {collapsedBundles.has(renderItem.item.Bundle ?? 'no-bundle') ? (
                                            <ExpandLess />
                                          ) : (
                                            <ExpandMore />
                                          )}
                                        </IconButton>
                                      </TableCell>
                                    ),
                                  )}
                                  <TableCell key="Actions">
                                    <IconButton
                                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                        setDrawerState({
                                          type: 'menu',
                                          lineItem: renderItem.item,
                                          anchorEl: e.currentTarget,
                                        })
                                      }}
                                    >
                                      <MoreVert />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Draggable>
                          )
                        } else {
                          // Line item
                          return (
                            <Draggable
                              key={renderItem.item.LineItemId}
                              draggableId={renderItem.item.LineItemId}
                              index={index}
                              isDragDisabled={!canDrag}
                            >
                              {provided => (
                                <TableRow
                                  className={`${
                                    renderItem.bundleId === null
                                      ? 'no-bundle'
                                      : isLastChildInBundle
                                      ? 'child-row-last'
                                      : ''
                                  } ${
                                    differences &&
                                    differences.additions.some(
                                      addition => addition.LineItemId === renderItem.item.LineItemId,
                                    ) &&
                                    additionsView
                                      ? 'additions'
                                      : ''
                                  } ${
                                    differences &&
                                    differences.deletions.some(
                                      deletion => deletion.LineItemId === renderItem.item.LineItemId,
                                    ) &&
                                    deletionsView
                                      ? 'deletions'
                                      : ''
                                  }`}
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  sx={{
                                    visibility: collapsedBundles.has(renderItem.bundleId ?? 'no-bundle')
                                      ? 'hidden'
                                      : 'visible',
                                    display: collapsedBundles.has(renderItem.bundleId ?? 'no-bundle')
                                      ? 'none'
                                      : 'table-row',
                                  }}
                                >
                                  {columns.map(column =>
                                    column.Name !== '#' ? (
                                      <TableCell
                                        className={
                                          differences &&
                                          differencesView &&
                                          differences.changes.some(change => {
                                            const changeWithDifferences = change as quoteLineType & {
                                              //eslint-disable-next-line @typescript-eslint/no-explicit-any
                                              columnDifferences?: Record<string, { previous: any; current: any }>
                                            }
                                            return (
                                              (changeWithDifferences.LineItemId === renderItem.item.LineItemId ||
                                                changeWithDifferences.MfrPartNumber ===
                                                  renderItem.item.MfrPartNumber) &&
                                              changeWithDifferences.columnDifferences?.[
                                                COLUMN_MAP[column.Name].property
                                              ]
                                            )
                                          })
                                            ? 'differences'
                                            : undefined
                                        }
                                        key={column.Name}
                                        align={
                                          COLUMN_MAP[column.Name].format === 'currency' ||
                                          COLUMN_MAP[column.Name].format === 'percentage' ||
                                          (COLUMN_MAP[column.Name].format === 'number' && column.Name !== '#')
                                            ? 'right'
                                            : 'left'
                                        }
                                        sx={{
                                          width: getColumnWidth(column.Width),
                                          minWidth: getColumnWidth(column.Width),
                                        }}
                                      >
                                        {getColumnValue(renderItem.item, column.Name)}
                                        {differences &&
                                        differencesView &&
                                        differences.changes.some(change => {
                                          const changeWithDifferences = change as quoteLineType & {
                                            //eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            columnDifferences?: Record<string, { previous: any; current: any }>
                                          }
                                          return (
                                            (changeWithDifferences.LineItemId === renderItem.item.LineItemId ||
                                              changeWithDifferences.MfrPartNumber === renderItem.item.MfrPartNumber) &&
                                            changeWithDifferences.columnDifferences?.[COLUMN_MAP[column.Name].property]
                                          )
                                        }) ? (
                                          <>
                                            <br />
                                            <DifferenceOutlined className="differences-icon" />
                                          </>
                                        ) : null}
                                      </TableCell>
                                    ) : (
                                      <TableCell
                                        key={column.Name}
                                        align={'left'}
                                        sx={{
                                          width: getColumnWidth(column.Width),
                                          minWidth: getColumnWidth(column.Width),
                                        }}
                                      >
                                        <Checkbox
                                          checked={selectedLineItems.some(
                                            selected => selected.LineItemId === renderItem.item.LineItemId,
                                          )}
                                          onChange={() => {
                                            setSelectedLineItems(prev => {
                                              const isSelected = prev.some(
                                                selected => selected.LineItemId === renderItem.item.LineItemId,
                                              )
                                              return isSelected
                                                ? prev.filter(
                                                    selected => selected.LineItemId !== renderItem.item.LineItemId,
                                                  )
                                                : [...prev, renderItem.item]
                                            })
                                          }}
                                        />{' '}
                                        {renderItem.item.LineNumber !== -1 ? renderItem.item.LineNumber : ''}
                                        {differences &&
                                        differences.additions.some(
                                          addition => addition.LineItemId === renderItem.item.LineItemId,
                                        ) &&
                                        additionsView ? (
                                          <>
                                            <br />
                                            <AddBoxOutlined className="additions-icon" />
                                          </>
                                        ) : differences &&
                                          differences.deletions.some(
                                            deletion => deletion.LineItemId === renderItem.item.LineItemId,
                                          ) &&
                                          deletionsView ? (
                                          <>
                                            <br />
                                            <DeleteOutlined className="deletions-icon" />
                                          </>
                                        ) : null}
                                      </TableCell>
                                    ),
                                  )}
                                  <TableCell key="Actions">
                                    <IconButton
                                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                        setDrawerState({
                                          type: 'menu',
                                          lineItem: renderItem.item,
                                          anchorEl: e.currentTarget,
                                        })
                                      }}
                                    >
                                      <MoreVert />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Draggable>
                          )
                        }
                      })}
                      {provided.placeholder}
                    </TableBody>
                  )
                }}
              </Droppable>
            )}
          </Table>
          <div className="quote-lines-add-line-items">
            <Fab
              variant="extended"
              size="large"
              onClick={() =>
                setDrawerState({ type: 'new', lineNumber: lineItems.filter(item => item.LineNumber !== -1).length })
              }
            >
              Add New Line {<Add />}
            </Fab>
          </div>
        </TableContainer>
      </DragDropContext>

      <Dialog open={openDialog === 'deleteView'} onClose={() => setOpenDialog('')} fullWidth>
        <DialogTitle variant="h6">Delete Template</DialogTitle>
        <DialogContent className="custom-view-dialog-content">
          <Typography>Are you sure you want to delete this template? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOpenDialog('')}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => deleteTemplate()} loading={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog === 'saveView'} onClose={() => setOpenDialog('')} fullWidth>
        <DialogTitle variant="h6">Save View</DialogTitle>
        <DialogContent className="custom-view-dialog-content">
          <Typography>Enter a view name:</Typography>
          <TextField fullWidth label="Name" size="small" value={viewName} onChange={e => setViewName(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOpenDialog('')}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => saveTemplate()} loading={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        open={openViewDrawer}
        onClose={() => setOpenViewDrawer(false)}
        anchor="right"
        className="custom-view-drawer"
      >
        <div className="custom-view-drawer-header">
          <Typography variant="h6">Edit Display Fields</Typography>
          <div className="custom-view-drawer-header-buttons">
            <Button
              variant="outlined"
              onClick={() => {
                const templateFields = selectedTemplate?.Columns.map(column => column.Name) ?? []
                setSelectedFields(templateFields)
                const widths: Record<string, number> = {}
                selectedTemplate?.Columns.forEach(column => {
                  widths[column.Name] = column.Width
                })
                setColumnWidths(widths)
              }}
            >
              Reset
            </Button>
            <Button variant="contained" onClick={() => applyView()}>
              Apply
            </Button>
          </div>
        </div>
        <div className="custom-view-drawer-content">
          <Table className="custom-view-drawer-table">
            <TableHead>
              <TableRow>
                <TableCell>Available Fields</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.keys(COLUMN_MAP)
                .filter(column => !selectedFields.includes(column))
                .map(column => (
                  <TableRow key={column}>
                    <TableCell>
                      {' '}
                      <Checkbox
                        checked={selectedAvailableFields.includes(column)}
                        onChange={() =>
                          selectedAvailableFields.includes(column)
                            ? setSelectedAvailableFields(prev => prev.filter(field => field !== column))
                            : setSelectedAvailableFields(prev => [...prev, column])
                        }
                      />{' '}
                      {column}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <div className="custom-view-drawer-buttons">
            <IconButton
              onClick={() => {
                const fieldsToAdd = Object.keys(COLUMN_MAP).filter(column => selectedAvailableFields.includes(column))
                setSelectedFields(prev => [...prev, ...fieldsToAdd])
                setColumnWidths(prev => {
                  const newWidths = { ...prev }
                  fieldsToAdd.forEach(field => {
                    if (!newWidths[field]) {
                      newWidths[field] = 1
                    }
                  })
                  return newWidths
                })
                setSelectedAvailableFields([])
              }}
            >
              <ChevronRight />
            </IconButton>
            <IconButton
              onClick={() => {
                setSelectedFields(prev => prev.filter(column => !selectedSelectedFields.includes(column)))
                setColumnWidths(prev => {
                  const newWidths = { ...prev }
                  selectedSelectedFields.forEach(field => {
                    delete newWidths[field]
                  })
                  return newWidths
                })
                setSelectedSelectedFields([])
              }}
            >
              <ChevronLeft />
            </IconButton>
          </div>
          <DragDropContext
            onDragEnd={result => {
              const newFields = selectedFields.map((column, index) => {
                if (index === result.source.index) {
                  return selectedFields[result.destination?.index ?? 0]
                }
                if (index === result.destination?.index) {
                  return selectedFields[result.source.index]
                }
                return column
              })
              setSelectedFields(newFields)
              setColumnWidths(prev => {
                const newWidths: Record<string, number> = {}
                newFields.forEach(field => {
                  newWidths[field] = prev[field] || 1
                })
                return newWidths
              })
            }}
          >
            <Table className="custom-view-drawer-table">
              <TableHead>
                <TableRow>
                  <TableCell>Selected Fields</TableCell>
                  <TableCell>Width</TableCell>
                </TableRow>
              </TableHead>
              <Droppable droppableId="selectedFields">
                {provided => (
                  <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                    {selectedFields.map((column, index) => (
                      <Draggable key={column} draggableId={column} index={index}>
                        {provided => (
                          <TableRow
                            key={column}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{ ...provided.draggableProps.style, cursor: 'grab' }}
                          >
                            <TableCell style={{ padding: '8px 0px', display: 'flex', alignItems: 'center', gap: 4 }}>
                              <DragIndicator fontSize="small" sx={{ color: 'grey.300' }} />
                              <Checkbox
                                checked={selectedSelectedFields.includes(column)}
                                onChange={() =>
                                  selectedSelectedFields.includes(column)
                                    ? setSelectedSelectedFields(prev => prev.filter(field => field !== column))
                                    : setSelectedSelectedFields(prev => [...prev, column])
                                }
                              />
                              {column}
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={columnWidths[column] || 1}
                                onChange={e => {
                                  const width = Math.max(1, Math.min(10, parseInt(e.target.value) || 1))
                                  setColumnWidths(prev => ({ ...prev, [column]: width }))
                                }}
                                inputProps={{ min: 1, max: 10, step: 1 }}
                                sx={{ width: '60px' }}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </Table>
          </DragDropContext>
        </div>
      </Drawer>

      <Menu
        open={drawerState.type === 'menu'}
        onClose={() => setDrawerState({ type: null })}
        anchorEl={drawerState.anchorEl}
        disableScrollLock={true}
      >
        <MenuItem onClick={() => handleMenuAction('edit')}>Edit</MenuItem>
        <MenuItem onClick={() => handleMenuAction('new')}>New Line</MenuItem>
        <MenuItem onClick={() => handleMenuAction('clone')}>Clone</MenuItem>
        {drawerState.lineItem?.Bundle && <MenuItem onClick={() => handleMenuAction('unbundle')}>Unbundle</MenuItem>}
        {drawerState.lineItem?.LineNumber !== -1 && (
          <MenuItem onClick={() => handleMenuAction('delete')}>Delete</MenuItem>
        )}
      </Menu>

      <Drawer open={drawerState.type === 'edit'} onClose={() => setDrawerState({ type: null })} anchor="right">
        <LineEdit
          type="single"
          open={drawerState.type === 'edit'}
          onClose={() => setDrawerState({ type: null })}
          selectedLineItem={drawerState.lineItem}
          fullLineItems={lineItems}
          setLineItems={setLineItems}
          quoteId={quoteId ?? ''}
        />
      </Drawer>
      <Drawer
        open={drawerState.type === 'new'}
        onClose={() => {
          setDrawerState({ type: null })
        }}
        anchor="right"
      >
        <LinesNewLine
          open={drawerState.lineNumber ?? 0}
          onClose={() => {
            setDrawerState({ type: null })
            setAddLineSnackbar('success')
          }}
          lineItems={lineItems}
          setLineItems={setLineItems}
          quoteId={quoteId ?? ''}
          bundle={drawerState.bundle ?? null}
        />
      </Drawer>

      <Alert
        severity="info"
        className="quote-lines-move-lines-alert"
        sx={{
          bottom:
            cloneSnackbar || deleteSnackbar || bulkEditSnackbar || bulkDeleteSnackbar || addLineSnackbar
              ? '90px'
              : '24px',
          opacity: canDrag ? 1 : 0,
          visibility: canDrag ? 'visible' : 'hidden',
        }}
      >
        <AlertTitle>Move Lines Enabled</AlertTitle>
        <Typography variant="body2">
          You can now move lines and bundles by dragging and dropping them. Changes will be saved automatically.
        </Typography>
      </Alert>
      <Snackbar
        autoHideDuration={5000}
        open={cloneSnackbar === 'success'}
        onClose={() => setCloneSnackbar(null)}
        message="Line cloned successfully"
      />
      <Snackbar open={cloneSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setCloneSnackbar(null)}>
          Failed to clone line item. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar
        open={deleteSnackbar === 'success'}
        onClose={() => setDeleteSnackbar(null)}
        message="Line item deleted successfully"
      />
      <Snackbar open={deleteSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setDeleteSnackbar(null)}>
          Failed to delete line item. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar
        autoHideDuration={5000}
        open={bulkEditSnackbar === 'success'}
        onClose={() => setBulkEditSnackbar(null)}
        message="Line items updated successfully"
      />
      <Snackbar open={bulkEditSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setBulkEditSnackbar(null)}>
          Failed to edit line items. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar
        autoHideDuration={5000}
        open={addLineSnackbar === 'success'}
        onClose={() => setAddLineSnackbar(null)}
        message="Line added successfully"
      />
      <Snackbar
        autoHideDuration={5000}
        open={bundleSnackbar === 'success'}
        onClose={() => setBundleSnackbar(null)}
        message="Line items bundled successfully"
      />
      <Snackbar
        autoHideDuration={5000}
        open={unbundleSnackbar === 'success'}
        onClose={() => setUnbundleSnackbar(null)}
        message="Line items unbundled successfully"
      />
      <Snackbar
        autoHideDuration={5000}
        open={unbundleSnackbar === 'fail'}
        onClose={() => setUnbundleSnackbar(null)}
        message="Error unbundling line items"
      />
      <Snackbar
        autoHideDuration={5000}
        open={bulkDeleteSnackbar === 'success'}
        onClose={() => setBulkDeleteSnackbar(null)}
        message="Bundle and line items deleted successfully"
      />
      <Snackbar
        autoHideDuration={5000}
        open={bulkDeleteSnackbar === 'fail'}
        onClose={() => setBulkDeleteSnackbar(null)}
        message="Error deleting bundle and line items"
      />
      <Snackbar open={bundleSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setBundleSnackbar(null)}>
          Failed to bundle line items. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar
        autoHideDuration={5000}
        open={saveTemplateSnackbar === 'success'}
        onClose={() => setSaveTemplateSnackbar(null)}
        message="Template saved successfully"
      />
      <Snackbar open={saveTemplateSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setSaveTemplateSnackbar(null)}>
          Failed to save template. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar
        autoHideDuration={5000}
        open={deleteTemplateSnackbar === 'success'}
        onClose={() => setDeleteTemplateSnackbar(null)}
        message="Template deleted successfully"
      />
      <Snackbar open={deleteTemplateSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setDeleteTemplateSnackbar(null)}>
          Failed to delete template. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </div>
  )
}

export default LinesCustomView
